import type { ReactElement } from "react";
import { useSession } from "@baseline/web/auth";
import { createFileRoute, Link } from "@tanstack/react-router";
import { MarkdownRenderer } from "../components/MarkdownRenderer";
import { SourceCard } from "../components/SourceCard";
import { useDocumentTitle } from "../hooks/useDocumentTitle";
import { useProject } from "../lib/useProject";
import type { ProjectDocument } from "../lib/useProject";

export const Route = createFileRoute("/project/$projectId")({
	component: ProjectDetailPage,
});

function timeAgo(iso: string): string {
	const diff = Date.now() - new Date(iso).getTime();
	const minutes = Math.floor(diff / 60000);
	if (minutes < 1) return "just now";
	if (minutes < 60) return `${minutes}m ago`;
	const hours = Math.floor(minutes / 60);
	if (hours < 24) return `${hours}h ago`;
	const days = Math.floor(hours / 24);
	if (days < 30) return `${days}d ago`;
	return new Date(iso).toLocaleDateString("en-US", {
		month: "short",
		day: "numeric",
	});
}

function DocCard({ doc }: { doc: ProjectDocument }): ReactElement {
	return (
		<Link
			to="/doc/$docId"
			params={{ docId: doc.id }}
			search={{ highlight: undefined }}
			className="border-line bg-surface hover:border-line-strong block rounded-lg border px-4 py-3 shadow-sm transition-colors"
		>
			<div className="flex items-start justify-between gap-2">
				<span className="text-fg-secondary line-clamp-2 min-w-0 text-sm leading-snug">
					{doc.title ?? "Untitled"}
				</span>
				{doc.doc_type && (
					<span className="bg-accent-wash text-accent shrink-0 rounded-full px-1.5 py-0.5 text-[9px] leading-none font-medium tracking-wider uppercase">
						{doc.doc_type}
					</span>
				)}
			</div>
			{doc.summary && (
				<p className="text-fg-ghost mt-1.5 line-clamp-2 text-xs leading-relaxed">
					{doc.summary}
				</p>
			)}
		</Link>
	);
}

function SectionHeader({
	id,
	children,
}: {
	id?: string;
	children: React.ReactNode;
}): ReactElement {
	return (
		<h2
			id={id}
			className="text-fg-ghost mb-4 text-[11px] tracking-widest uppercase"
		>
			{children}
		</h2>
	);
}

function ProjectDetailPage(): ReactElement {
	const { projectId } = Route.useParams();
	const { data: session } = useSession();
	const projectQuery = useProject(projectId, session?.user?.id);

	useDocumentTitle(projectQuery.data?.name ?? "Project");

	if (projectQuery.isPending) {
		return (
			<div className="mx-auto max-w-[960px] px-6 pt-8">
				<div className="mb-8">
					<div className="bg-skeleton mb-2 h-7 w-48 animate-pulse rounded" />
					<div className="bg-skeleton h-4 w-72 animate-pulse rounded" />
				</div>
				<div className="bg-skeleton h-64 animate-pulse rounded-lg" />
			</div>
		);
	}

	if (!projectQuery.data) {
		return (
			<div className="mx-auto max-w-[960px] px-6 py-10">
				<div className="border-line-subtle bg-surface-raised rounded-lg border p-8 text-center">
					<h1 className="font-display text-fg-strong text-xl">
						Project not found
					</h1>
					<p className="text-fg-faint mt-2">
						This project may not exist or you don&apos;t have access
						to it.
					</p>
					<Link
						to="/"
						search={{
							mode: undefined,
							tag: undefined,
							type: undefined,
							summary: undefined,
						}}
						className="text-accent hover:text-fg-strong mt-4 inline-block transition-colors"
					>
						Go back home
					</Link>
				</div>
			</div>
		);
	}

	const project = projectQuery.data;
	const { synthesisDoc, documents, sources } = project;
	const isSynthesizing = project.synthesis_due_at !== null;

	return (
		<div className="pb-24">
			{/* Header */}
			<section className="mx-auto max-w-[960px] px-6 pt-8">
				<div className="mb-1 flex items-start justify-between gap-4">
					<div className="min-w-0 flex-1">
						<Link
							to="/"
							search={{
								mode: undefined,
								tag: undefined,
								type: undefined,
								summary: undefined,
							}}
							className="text-fg-ghost hover:text-fg-secondary mb-2 inline-block text-xs transition-colors"
						>
							&larr; Projects
						</Link>
						<h1 className="font-display text-fg-strong text-xl font-semibold">
							{project.name}
						</h1>
					</div>
					{sources.length > 0 && (
						<button
							type="button"
							onClick={() => {
								document
									.getElementById("sources-section")
									?.scrollIntoView({ behavior: "smooth" });
							}}
							className="text-fg-ghost hover:text-fg-secondary shrink-0 text-xs transition-colors"
						>
							{sources.length} source
							{sources.length !== 1 ? "s" : ""} &darr;
						</button>
					)}
				</div>
				<div className="text-fg-ghost flex items-center gap-3 text-xs">
					{project.description && (
						<span className="text-fg-tertiary">
							{project.description}
						</span>
					)}
					{project.last_synthesized_at && (
						<span>
							Synthesized {timeAgo(project.last_synthesized_at)}
						</span>
					)}
					{isSynthesizing && (
						<span className="text-accent animate-pulse">
							Synthesizing...
						</span>
					)}
				</div>
			</section>

			<div className="mx-auto max-w-[960px] px-6">
				{/* Synthesis Document */}
				{synthesisDoc?.content && (
					<section className="mt-8">
						<div className="border-accent-wash bg-surface-raised rounded-lg border p-6">
							<div className="mb-4 flex items-center justify-between">
								<span className="text-accent text-[11px] tracking-widest uppercase">
									Synthesis
								</span>
								<Link
									to="/doc/$docId"
									params={{ docId: synthesisDoc.id }}
									search={{ highlight: undefined }}
									className="text-fg-ghost hover:text-accent text-xs transition-colors"
								>
									Open full doc &rarr;
								</Link>
							</div>
							<MarkdownRenderer
								markdown={synthesisDoc.content}
								className="synthesis-content"
							/>
						</div>
					</section>
				)}

				{/* Documents */}
				{documents.length > 0 && (
					<section className="mt-10">
						<SectionHeader>Documents</SectionHeader>
						<div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
							{documents.map((doc) => (
								<DocCard key={doc.id} doc={doc} />
							))}
						</div>
					</section>
				)}

				{/* Sources */}
				{sources.length > 0 && (
					<section id="sources-section" className="mt-10">
						<SectionHeader>Sources</SectionHeader>
						<div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
							{sources.map((source) => (
								<SourceCard key={source.id} source={source} />
							))}
						</div>
					</section>
				)}

				{/* Empty state */}
				{!synthesisDoc &&
					documents.length === 0 &&
					sources.length === 0 && (
						<div className="border-line-subtle bg-surface-raised mt-8 rounded-lg border p-8 text-center">
							<p className="text-fg-faint">
								This project has no items yet.
							</p>
						</div>
					)}
			</div>
		</div>
	);
}
